'use client';

import { useEffect, useState } from 'react';
import Image from 'next/image';
import { Box } from '@mui/material';

const HERO_PHOTO = '/images/marketing/hero-creator-laptop.png';

type Props = {
  spotlightSrc: string;
  spotlightAlt: string;
  photoAlt?: string;
};

function HeroVisualPlaceholder() {
  return (
    <Box
      aria-hidden
      sx={{
        width: '100%',
        minHeight: { xs: 320, md: 440 },
      }}
    >
      <Box
        sx={{
          width: '100%',
          height: '100%',
          minHeight: { xs: 320, md: 440 },
          borderRadius: 3,
          bgcolor: 'grey.100',
        }}
      />
    </Box>
  );
}

/**
 * Hootsuite-style hero collage: photo fills the column; product UI sits in a
 * corner (bottom-left) so it never covers the subject's face.
 */
export function MarketingHeroVisual({
  spotlightSrc,
  spotlightAlt,
  photoAlt = 'Creator working on a laptop',
}: Props) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  if (!mounted) {
    return <HeroVisualPlaceholder />;
  }

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        minHeight: { xs: 340, md: 460 },
        py: { xs: 1, md: 0 },
      }}
    >
      {/* Stage — fills the full grid column */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: { xs: 340, md: 460 },
          borderRadius: 3,
          overflow: 'visible',
        }}
      >
        {/* Lifestyle photo (face kept upper-right via object-position) */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            borderRadius: 3,
            overflow: 'hidden',
            bgcolor: 'grey.100',
            boxShadow: (t) =>
              `0 24px 48px ${t.palette.mode === 'light' ? 'rgba(15, 23, 42, 0.1)' : 'rgba(0,0,0,0.35)'}`,
          }}
        >
          <Image
            src={HERO_PHOTO}
            alt={photoAlt}
            fill
            sizes="(max-width: 900px) 100vw, 50vw"
            style={{ objectFit: 'cover', objectPosition: '62% 28%' }}
            priority
          />
        </Box>

        {/* UI spotlight — bottom-left, away from face (Hootsuite places accents on periphery) */}
        <Box
          sx={{
            position: 'absolute',
            left: { xs: 8, md: 12 },
            bottom: { xs: -12, md: -20 },
            width: { xs: '46%', sm: '42%', md: '40%' },
            maxWidth: 220,
            zIndex: 2,
            filter: 'drop-shadow(0 12px 28px rgba(15, 23, 42, 0.2))',
            transform: { md: 'rotate(-1.5deg)' },
          }}
        >
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              aspectRatio: '374 / 420',
            }}
          >
            <Image
              src={spotlightSrc}
              alt={spotlightAlt}
              fill
              sizes="(max-width: 900px) 45vw, 220px"
              style={{ objectFit: 'contain', objectPosition: 'center' }}
              priority
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
